import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, ArrowRight, Star, Heart, Zap, Shield, MessageCircle, ChevronDown, ChevronUp, User } from "lucide-react";
import Footer from "../components/Footer";
import PublicNav from "../components/PublicNav";

const HomePage: React.FC = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "Hoe werkt Solofix precies?",
      answer: "Solofix maakt het gemakkelijk om eventgroepen te vinden en te joinen. Maak een profiel, kies je favoriete evenementen, bekijk beschikbare groepen en sluit je aan bij mensen die je leuk lijken. Chat met je nieuwe eventgroep voor, tijdens en na het evenement!"
    },
    {
      question: "Is Solofix gratis te gebruiken?",
      answer: "Ja! Solofix is volledig gratis te gebruiken. Je kunt groepen joinen, chatten met andere eventbezoekers en je profiel aanmaken zonder kosten. Er zijn een aantal optionele extra's waarmee je het platform kunt steunen, maar die zijn vrijwillig en hebben geen grote invloed op de werking van de app."
    },
    {
      question: "Hoe veilig is het om vreemden te ontmoeten?",
      answer: "Veiligheid staat bij ons voorop. Alle gebruikers moeten zich verifiëren met een email adres. Je kunt profielen bekijken, chatten voordat je elkaar ontmoet, en groepen hebben vaak meerdere leden. Vertrouw altijd op je gevoel en ontmoet elkaar op veilige, openbare plekken op evenementen."
    },
    {
      question: "Voor welke evenementen kan ik Solofix gebruiken?",
      answer: "Solofix ondersteunt alle soorten evenementen. Van concerten en festivals tot conferenties, sportevenementen en workshops. Je kunt zelf namelijk een event aanmaken! Het staat meteen live, en ons team controleert het achteraf nog even mee."
    },
    {
      question: "Wat als ik geen groep vind die bij me past?",
      answer: "Geen probleem! Je kunt zelf een nieuwe groep opstarten voor elk evenement. Beschrijf wat je zoekt - bijvoorbeeld je interesses, activiteiten, leeftijdsgroep - en andere eventbezoekers kunnen zich bij jou aansluiten."
    },
    {
      question: "Kan ik alleen gebruiken op mijn telefoon?",
      answer: "Solofix werkt perfect op zowel je telefoon als computer. Onze website is volledig responsive, dus je kunt overal en altijd contact maken met je eventgroep, plannen maken en chatten."
    }
  ];


  const features = [
    {
      icon: Calendar,
      title: "Vind je event",
      description: "Zoek je favoriete evenement of activiteit. Niet gevonden? Maak het zelf aan zodat anderen zich kunnen aansluiten.",
      tile: "bg-avatar-pink",
      iconColor: "text-white"
    },
    {
      icon: Users,
      title: "Groep vinden of maken",
      description: "Sluit je aan bij bestaande groepen of start je eigen groep. Vind mensen die jouw interesses delen.",
      tile: "bg-avatar-cyan",
      iconColor: "text-ink"
    },
    {
      icon: MessageCircle,
      title: "Live groepschat",
      description: "Blijf verbonden met je groep via een groepschat. Plan activiteiten, overleg waar je afspreekt, maak plannen.",
      tile: "bg-avatar-violet",
      iconColor: "text-white"
    },
    {
      icon: Star,
      title: "Multi-event platform",
      description: "Van concerten tot conferenties, van sportevenementen tot workshops - overal kun je een groep voor aanmaken.",
      tile: "bg-avatar-pink",
      iconColor: "text-white"
    },
    {
      icon: Shield,
      title: "Veilige omgeving",
      description: "Jij kiest zelf met wie je in contact komt, en bepaalt zelf wat je deelt met anderen.",
      tile: "bg-avatar-cyan",
      iconColor: "text-ink"
    },
    {
      icon: Heart,
      title: "Persoonlijke chat",
      description: "Chat individueel met leden van je groep. Maak persoonlijke connecties en diepere vriendschappen.",
      tile: "bg-avatar-violet",
      iconColor: "text-white"
    }
  ];

  const steps = [
    {
      step: "01",
      title: "Registreer gratis",
      description: "Vertel over jezelf, je interesses en welke evenementen je leuk vindt. Zoek het event waar je heen wil, of maak er zelf één aan.",
      icon: User,
      tile: "bg-avatar-pink",
      iconColor: "text-white"
    },
    {
      step: "02",
      title: "Vind je groep",
      description: "Zoek een bestaande groep en bekijk de profielen van de leden. Geen match? Maak je eigen!",
      icon: Users,
      tile: "bg-avatar-violet",
      iconColor: "text-white"
    },
    {
      step: "03",
      title: "Connect!",
      description: "Start de groepschat, leer elkaar kennen en bepaal of dit het gezelschap is waar je mee op pad wil.",
      icon: Zap,
      tile: "bg-avatar-cyan",
      iconColor: "text-ink"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <PublicNav />

      {/* Hero Section */}
      <section className="pt-36 pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="sf-eyebrow">// nooit meer solo</div>
                <h1 className="text-5xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-fg-bright leading-[1.05]">
                  Ga <span className="sf-gradient-text">nooit meer alleen</span> naar iets toe!
                </h1>

                <p className="text-lg text-fg-muted leading-relaxed max-w-2xl">
                  Ken je dat? Je wil naar een festival of evenement, en niemand wil met je mee? Tijd om gemakkelijk en gratis een groep te vinden (of maken) met Solofix!
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="group sf-btn-primary animate-neon-pulse px-8 py-4 text-lg"
                >
                  Fix going solo!
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/login"
                  className="sf-btn-secondary px-8 py-4 text-lg"
                >
                  Al een account? Log in
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-8 bg-neon-gradient opacity-20 blur-3xl rounded-full pointer-events-none"></div>
              <div className="relative z-10 rounded-card border border-white/[.08] bg-surface shadow-panel overflow-hidden">
                <div className="border-b border-white/[.08] p-4" style={{ background: 'linear-gradient(90deg, rgba(255,61,129,.12), rgba(35,229,219,.08))' }}>
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-avatar-pink">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="text-fg-bright font-semibold">Event Groep "Music Lovers"</div>
                      <div className="sf-label-mono mt-0.5">4 van 8 leden</div>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="sf-item border border-white/[.08] p-3">
                    <div className="text-fg-soft text-sm">🎵 "Wie is er klaar voor vanavond?!"</div>
                    <div className="font-mono text-[11px] text-fg-meta mt-1.5 uppercase tracking-[.14em]">Lisa • 2 min geleden</div>
                  </div>
                  <div className="sf-item border border-white/[.08] p-3">
                    <div className="text-fg-soft text-sm">🔥 "Ik heb de beste plek gevonden!"</div>
                    <div className="font-mono text-[11px] text-fg-meta mt-1.5 uppercase tracking-[.14em]">Mark • 5 min geleden</div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    {[1,2,3,4].map(i => (
                      <div key={i} className={`w-9 h-9 rounded-full flex items-center justify-center text-sm ${i <= 2 ? 'bg-avatar-pink text-white' : 'border border-dashed border-white/[.16] text-fg-faint'}`}>
                        {i <= 2 ? '👤' : '?'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <div className="sf-eyebrow">// features</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-fg-bright">
              Alles wat je nodig hebt voor <span className="sf-gradient-text">epic events</span>
            </h2>
            <p className="text-lg text-fg-muted max-w-3xl mx-auto">
              Van het vinden van je perfecte groep tot het maken van onvergetelijke herinneringen - Solofix heeft alles onder één dak.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="sf-card p-6 transition-colors duration-300 hover:bg-white/[.05]">
                <div className={`grid h-11 w-11 place-items-center rounded-[14px] ${feature.tile} mb-4`}>
                  <feature.icon className={`h-5 w-5 ${feature.iconColor}`} />
                </div>
                <h3 className="text-lg font-semibold text-fg-bright mb-3">{feature.title}</h3>
                <p className="text-fg-body text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <div className="sf-eyebrow">// zo werkt het</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-fg-bright">
              Zo simpel werkt het
            </h2>
            <p className="text-lg text-fg-muted">In 3 stappen naar je perfecte crew</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <div key={index} className="relative">
                {index < 2 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-neon-gradient opacity-30 transform translate-x-4"></div>
                )}
                <div className="text-center space-y-6">
                  <div className="relative inline-block">
                    <div className={`w-24 h-24 mx-auto ${item.tile} rounded-full flex items-center justify-center shadow-avatar-glow`}>
                      <item.icon className={`h-10 w-10 ${item.iconColor}`} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="sf-label-mono">Stap {item.step}</div>
                    <h3 className="text-2xl font-bold tracking-tight text-fg-bright">{item.title}</h3>
                    <p className="text-fg-muted max-w-sm mx-auto">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="sf-card-highlight relative overflow-hidden text-center px-6 py-16 sm:px-12 space-y-8">
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-neon-pink/20 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-neon-cyan/15 blur-3xl pointer-events-none"></div>
            <div className="relative space-y-6">
              <div className="sf-eyebrow">// let's go</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-fg-bright">
                Klaar om er samen een <br />
                <span className="sf-gradient-text">feestje van te maken?</span>
              </h2>
              <p className="text-lg text-fg-muted max-w-2xl mx-auto">
                Solofix is nieuw, en gratis! Maak je event aan, vorm je groep en deel de link met anderen. Niet geschoten, altijd mis!
              </p>
              <Link
                to="/register"
                className="sf-btn-primary animate-neon-pulse px-10 py-4 text-lg"
              >
                Registreer gratis
                <ArrowRight className="ml-3 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <div className="sf-eyebrow">// faq</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-fg-bright">
              Veelgestelde vragen
            </h2>
            <p className="text-lg text-fg-muted">
              Alles wat je wilt weten over Solofix
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="sf-card overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/[.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-pink/50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-fg-bright pr-8">
                    {faq.question}
                  </h3>
                  {expandedFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-neon-cyan flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-neon-cyan flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-fg-body leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
